import React, {useEffect, useState} from 'react'
import {ProjectGuestPortalClient} from '../../infrastracture/api/ProjectGuestPortalClient'
import {useProjectGuest} from './ProjectGuestContext'

function toDateInputValue(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// Auto-booking already runs for guests, so this is a "change" flow in the normal case, not a
// first-booking flow, but the same switch-asset call also works with no existing booking
// (SwitchProjectAssetCommand tolerates zero existing check-ins for the date), so it doubles as
// a plain booking flow when auto-booking didn't find a desk. Phase 4.4.
export function ProjectGuestBook() {
  const {selectedProject, selectedProjectId, loading: projectsLoading} = useProjectGuest()
  const [date, setDate] = useState(toDateInputValue(new Date()))
  const [assets, setAssets] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [bookingAssetId, setBookingAssetId] = useState('')

  const load = async () => {
    if (!selectedProjectId) return
    setLoading(true)
    setError('')
    const response = await ProjectGuestPortalClient.GetAvailableAssets(selectedProjectId, date)
    if (response.successful) setAssets(response.data || [])
    else setError('Failed to load available assets for this date.')
    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, date])

  const handleBook = async (assetId: string) => {
    if (!selectedProjectId) return
    setBookingAssetId(assetId)
    setError('')
    setSuccess('')
    const response = await ProjectGuestPortalClient.SwitchAsset(selectedProjectId, assetId, date)
    setBookingAssetId('')
    if (response.successful) {
      setSuccess('Booked successfully.')
      await load()
    } else {
      setError(
        response.validationErrors?.[0]?.message ||
          response.data?.message ||
          'Failed to book this asset.'
      )
    }
  }

  const minDate = selectedProject?.startDate ? toDateInputValue(new Date(selectedProject.startDate)) : undefined
  const maxDate = selectedProject?.endDate ? toDateInputValue(new Date(selectedProject.endDate)) : undefined

  return (
    <div className='card'>
      <div className='card-header'>
        <div className='card-title'>
          <h3>Book an Asset</h3>
        </div>
        <div className='card-toolbar'>
          <input
            type='date'
            className='form-control form-control-sm w-auto'
            value={date}
            min={minDate}
            max={maxDate}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
      </div>
      {error && (
        <div className='px-9 pt-3'>
          <span className='text-danger'>{error}</span>
        </div>
      )}
      {success && (
        <div className='px-9 pt-3'>
          <span className='text-success'>{success}</span>
        </div>
      )}
      <div className='card-body py-3'>
        <div className='table-responsive'>
          <table className='table align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold text-muted bg-light'>
                <th className='ps-4 min-w-200px rounded-start'>Asset</th>
                <th className='min-w-150px'>Type</th>
                <th className='min-w-120px'>Site</th>
                <th className='min-w-120px'>Level</th>
                <th className='min-w-100px text-end rounded-end'></th>
              </tr>
            </thead>
            <tbody>
              {!projectsLoading &&
                !loading &&
                assets.map((asset: any) => (
                  <tr key={asset.assetId}>
                    <td className='ps-4'>
                      {asset.assetLetter ? `${asset.assetLetter}: ` : ''}
                      {asset.assetName}
                    </td>
                    <td>{asset.assetTypeName}</td>
                    <td>{asset.siteName}</td>
                    <td>{asset.floorName}</td>
                    <td className='text-end'>
                      <button
                        type='button'
                        className='btn btn-sm btn-primary'
                        disabled={bookingAssetId === asset.assetId}
                        onClick={() => handleBook(asset.assetId)}
                      >
                        Book
                      </button>
                    </td>
                  </tr>
                ))}
              {!projectsLoading && !loading && assets.length === 0 && (
                <tr>
                  <td colSpan={5} className='text-center text-muted py-5'>
                    No assets available for this date
                  </td>
                </tr>
              )}
              {(projectsLoading || loading) && (
                <tr>
                  <td colSpan={5} className='text-center text-muted py-5'>
                    Loading...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
